import React, { useContext, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Center,
  Flex,
  Heading,
  HStack,
  Icon,
  Skeleton,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { FiFilePlus, FiSend, FiStopCircle, FiUser } from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "react-query";
import LayoutView from "../../components/layout";
import Auth from "../../hoc/Auth";
import Biodata from "./sections/Biodata";
import ContinuationSheet from "./sections/ContinuatioSheet/index";
import Timeline from "./sections/Timeline";
import Forward from "../../components/forms/Forward";
import EndModal from "./sections/EndModal";
import { GlobalContext } from "../../context/Provider";
import ConfirmModal from "./sections/ConfirmModal";
import {
  forwardPatientApi,
  getAppointmentsByIdApi,
  openAppointmentApi,
} from "../../api/appointments";
import RequestFormDrawer from "../../components/navbar/sections/requestForms";
import { PERMISSIONS } from "../../auth/permissions";

const patientName = (patient) =>
  `${patient?.bioData?.surname || ""} ${patient?.bioData?.otherNames || ""}`.trim() ||
  "Unknown patient";

const Appointment = () => {
  const navigate = useNavigate();
  const [tabIndex, setTabIndex] = useState(0);
  const toast = useToast();
  const appointmentId = useParams().id;
  const forwardModal = useDisclosure();
  const endModal = useDisclosure();
  const requestFormDrawer = useDisclosure();
  const confirmModal = useDisclosure();
  const { authState, can } = useContext(GlobalContext);

  const appointmentQuery = useQuery(
    ["getAppointmentsByIdApi", appointmentId],
    () => getAppointmentsByIdApi(appointmentId),
  );
  const appointment = appointmentQuery.data?.data?.appointment;
  const patient = appointment?.patient;
  const currentWorker = appointment?.currentAttendingWorker;
  const isCurrentWorker = currentWorker?._id === authState.user?._id;
  const canReopen = can(PERMISSIONS.APPOINTMENTS_REOPEN);
  const canHandle = can(PERMISSIONS.APPOINTMENTS_HANDLE);
  const canAssess = can(PERMISSIONS.ASSESSMENTS_CREATE);
  const canManageRequestForms = can(PERMISSIONS.REQUEST_FORMS_MANAGE_ASSIGNED);

  const forwardMutation = useMutation(forwardPatientApi, {
    onSuccess: () => {
      forwardModal.onClose();
      toast({
        title: "Patient forwarded",
        description:
          "The selected staff member is now responsible for this attendance.",
        status: "success",
        duration: 3500,
        isClosable: true,
      });
      navigate("/appointments");
    },
    onError: (error) =>
      toast({
        title: "Unable to forward patient",
        description: error.response?.data?.msg || "Please try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      }),
  });

  const reopenMutation = useMutation(openAppointmentApi, {
    onSuccess: async () => {
      await appointmentQuery.refetch();
      toast({
        title: "Attendance reopened",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error) =>
      toast({
        title: "Unable to reopen attendance",
        description: error.response?.data?.msg || "Please try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      }),
  });

  const tabs = [
    {
      label: "Care timeline",
      description: "Handoffs and clinical forms",
      component: (
        <Timeline
          refetch={appointmentQuery.refetch}
          appointment={appointment}
        />
      ),
    },
    {
      label: "Doctor assessments",
      description: "Findings, diagnoses and care plan",
      component: (
        <ContinuationSheet
          appointment={appointment}
          canEdit={canAssess && !appointment?.closed && isCurrentWorker}
          refetch={appointmentQuery.refetch}
        />
      ),
    },
    {
      label: "Patient biodata",
      description: "Identity and contact details",
      component: <Biodata bioData={patient?.bioData} patient={patient} />,
    },
  ];

  if (appointmentQuery.isLoading) {
    return (
      <LayoutView activePage="Appointment Attendance">
        <Stack spacing="5">
          <Skeleton h="130px" borderRadius="2xl" />
          <Skeleton h="64px" borderRadius="xl" />
          <Skeleton h="360px" borderRadius="2xl" />
        </Stack>
      </LayoutView>
    );
  }

  if (appointmentQuery.isError || !appointment) {
    return (
      <LayoutView activePage="Appointment Attendance">
        <Center minH="55vh" flexDirection="column" textAlign="center">
          <Heading size="md">Attendance could not be loaded</Heading>
          <Text mt="2" color="gray.500">
            It may no longer exist, or your connection was interrupted.
          </Text>
          <HStack mt="5">
            <Button variant="outline" onClick={() => navigate("/appointments")}>
              Back to appointments
            </Button>
            <Button onClick={() => appointmentQuery.refetch()}>
              Try again
            </Button>
          </HStack>
        </Center>
      </LayoutView>
    );
  }

  return (
    <LayoutView activePage="Appointment Attendance">
      <Stack spacing="5">
        <Box
          bg="white"
          borderWidth="1px"
          borderColor="gray.200"
          borderRadius="2xl"
          p={{ base: "4", md: "6" }}
        >
          <Flex
            justify="space-between"
            align={{ base: "flex-start", lg: "center" }}
            gap="5"
            direction={{ base: "column", lg: "row" }}
          >
            <HStack spacing="4" align="flex-start">
              <Button
                aria-label="Back to appointments"
                variant="ghost"
                px="2"
                onClick={() => navigate(-1)}
              >
                <ArrowBackIcon boxSize="5" />
              </Button>
              <Box>
                <HStack flexWrap="wrap">
                  <Heading size="md" textTransform="capitalize">
                    {patientName(patient)}
                  </Heading>
                  <Badge
                    colorScheme={appointment.closed ? "red" : "green"}
                    borderRadius="full"
                    px="3"
                    py="1"
                  >
                    {appointment.closed ? "Closed" : "Active attendance"}
                  </Badge>
                </HStack>
                <Text mt="2" color="gray.500" fontSize="sm">
                  Patient ID:{" "}
                  {patient?.patientNumber ||
                    patient?.bioData?.unitNo ||
                    patient?._id}
                </Text>
                {!appointment.closed && currentWorker && (
                  <Text mt="1" color="gray.600" fontSize="sm">
                    Currently with{" "}
                    <Text as="span" fontWeight="700">
                      {isCurrentWorker
                        ? "you"
                        : `${currentWorker.firstName} ${currentWorker.lastName}`}
                    </Text>
                    {currentWorker.roomNumber && (
                      <Text as="span" fontWeight="700" color="brand.600">
                        {` · Room ${String(currentWorker.roomNumber).replace(/^room\s*/i, "")}`}
                      </Text>
                    )}
                  </Text>
                )}
              </Box>
            </HStack>

            <Flex gap="2" flexWrap="wrap" w={{ base: "full", lg: "auto" }}>
              <Button
                variant="outline"
                leftIcon={<FiUser />}
                onClick={() => navigate(`/patient/${patient?._id}`)}
              >
                Patient record
              </Button>
              {!appointment.closed && isCurrentWorker && canHandle && (
                <>
                  {canManageRequestForms && (
                    <Button
                      variant="outline"
                      leftIcon={<FiFilePlus />}
                      onClick={requestFormDrawer.onOpen}
                    >
                      Request form
                    </Button>
                  )}
                  <Button leftIcon={<FiSend />} onClick={forwardModal.onOpen}>
                    Forward
                  </Button>
                  <Button
                    colorScheme="red"
                    variant="outline"
                    leftIcon={<FiStopCircle />}
                    onClick={endModal.onOpen}
                  >
                    End attendance
                  </Button>
                </>
              )}
            </Flex>
          </Flex>
        </Box>

        {appointment.closed ? (
          <Alert status="info" borderRadius="xl" p="5" alignItems="flex-start">
            <AlertIcon mt="1" />
            <Box flex="1">
              <Heading size="sm">This attendance has ended</Heading>
              <AlertDescription mt="1">
                The timeline remains available as a read-only clinical record.
              </AlertDescription>
            </Box>
            {canReopen && (
              <Button
                size="sm"
                ml="4"
                isLoading={reopenMutation.isLoading}
                onClick={() => reopenMutation.mutate(appointmentId)}
              >
                Reopen attendance
              </Button>
            )}
          </Alert>
        ) : !isCurrentWorker ? (
          <Alert
            status="warning"
            borderRadius="xl"
            p="5"
            alignItems="flex-start"
          >
            <AlertIcon mt="1" />
            <Box flex="1">
              <Heading size="sm">
                This attendance is assigned to another staff member
              </Heading>
              <AlertDescription mt="1">
                You can review the record, but must claim it before adding forms
                or making changes.
              </AlertDescription>
            </Box>
            {canHandle && (
              <Button size="sm" ml="4" onClick={confirmModal.onOpen}>
                Claim attendance
              </Button>
            )}
          </Alert>
        ) : null}

        <Tabs index={tabIndex} onChange={setTabIndex} isLazy variant="unstyled">
          <Box
            bg="white"
            borderWidth="1px"
            borderColor="gray.200"
            borderRadius="xl"
            p="2"
            overflowX="auto"
          >
            <TabList gap="2" minW="max-content">
              {tabs.map((item, index) => (
                <Tab
                  key={item.label}
                  borderRadius="lg"
                  px="5"
                  py="3"
                  textAlign="left"
                  _selected={{ bg: "brand.50", color: "brand.700" }}
                >
                  <Box>
                    <Text fontWeight="800" fontSize="sm">
                      {item.label}
                    </Text>
                    <Text
                      mt="0.5"
                      fontSize="xs"
                      color={tabIndex === index ? "brand.600" : "gray.500"}
                    >
                      {item.description}
                    </Text>
                  </Box>
                </Tab>
              ))}
            </TabList>
          </Box>
          <TabPanels>
            {tabs.map((item) => (
              <TabPanel key={item.label} px="0" py="5">
                {item.component}
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      </Stack>

      <Forward
        heading="Forward patient"
        description="Choose the staff member who should take over this attendance."
        onForward={(workerId) =>
          forwardMutation.mutate({
            nextWorker: workerId,
            appointmentId: appointment._id,
          })
        }
        isLoading={forwardMutation.isLoading}
        forwardModal={forwardModal}
      />
      <EndModal appointment={appointment} endModal={endModal} />
      <ConfirmModal
        refetch={appointmentQuery.refetch}
        appointment={appointment}
        confirmModal={confirmModal}
      />
      <RequestFormDrawer
        patient={patient}
        requestFormDrawer={requestFormDrawer}
      />
    </LayoutView>
  );
};

export default Auth(Appointment);
